import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Mail, Phone, Instagram, Facebook } from 'lucide-react';

export const Footer = forwardRef<HTMLElement>((_, ref) => {
  const { t } = useTranslation();

  return (
    <footer ref={ref} className="bg-charcoal text-ivory">
      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-3">
              <h3 className="font-heading text-xl font-semibold text-ivory">Sampaoli</h3>
              <p className="font-elegant text-xs text-cream/80 italic">{t('hero.subtitle')}</p>
            </Link>
            <div className="flex items-center gap-2 text-cream/70">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="font-body text-xs">{t('about.location')}</span>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="font-heading text-sm font-medium text-ivory mb-3">
              {t('footer.navigation')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/shop" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('nav.shop')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Collections Column */}
          <div>
            <h4 className="font-heading text-sm font-medium text-ivory mb-3">
              {t('footer.collections')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/collections/mountain" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.mountain.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/fauna" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.fauna.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/equestrian" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.equestrian.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/gifts-for-hunters" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.giftsHunters.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/gifts-for-climbers" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.giftsClimbers.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/carabiner-necklaces" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.carabinerNecklaces.name')}
                </Link>
              </li>
              <li>
                <Link to="/collections/equestrian-jewelry" className="font-body text-xs text-cream/70 hover:text-ivory transition-colors">
                  {t('collections.equestrianJewelry.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Social Column */}
          <div>
            <h4 className="font-heading text-sm font-medium text-ivory mb-3">
              {t('footer.contact')}
            </h4>
            <div className="space-y-2 mb-4">
              <a
                href="mailto:info@sampaolicreazioni.it"
                className="flex items-center gap-2 text-cream/50 hover:text-ivory transition-colors"
              >
                <Mail className="h-3 w-3 flex-shrink-0" />
                <span className="font-body text-xs">info@sampaolicreazioni.it</span>
              </a>
              <a
                href="tel:+393487630428"
                className="flex items-center gap-2 text-cream/50 hover:text-ivory transition-colors"
              >
                <Phone className="h-3 w-3 flex-shrink-0" />
                <span className="font-body text-xs">+39 348 763 0428</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/sampaoli_italian_creations/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/70 hover:text-ivory transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/andrea.sampaoli.creazioni/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/70 hover:text-ivory transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-4 border-t border-leather">
          <p className="font-body text-xs text-cream/60 text-center">
            © {new Date().getFullYear()} Sampaoli Creations. {t('footer.rights')}.
          </p>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';